<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'

const props = defineProps<{
  context: FormKitFrameworkContext
}>()

const isHtmlMode = ref(false)
const isModalOpen = ref(false)

interface MenuBarItem {
  icon?: string
  title: string
  action?: () => void
  isActive?: () => boolean
}
const htmlContent = ref(props.context.value) //ref(editor.value?.getHTML().replace(/  /g, '&nbsp;&nbsp;'))

const hasArbitraryHtml = computed(() => {
  return (
    htmlContent.value?.includes('<script') ||
    htmlContent.value?.includes('<div') ||
    htmlContent.value?.includes('<center') ||
    htmlContent.value?.includes('<font') ||
    htmlContent.value?.includes('<span') ||
    htmlContent.value?.includes('<table') ||
    htmlContent.value?.includes('<iframe')
  )
})

const editor = useEditor({
  content: !hasArbitraryHtml.value
    ? props.context._value
    : '<p>HTML Mode Only</p>',
  extensions: [
    StarterKit,
    // Link.configure({
    //   openOnClick: false,
    //   autolink: true,
    //   defaultProtocol: 'https',
    // }),
  ],
  editorProps: {
    attributes: {
      class: 'prose prose-sm md:prose m-5 focus:outline-none min-h-[250px]',
    },
  },
  onFocus(p) {
    props.context.node.input(
      editor.value.getHTML().replace(/  /g, '&nbsp;&nbsp;'),
    )
  },
  onBlur: (p) => {
    props.context.node.input(
      editor.value.getHTML().replace(/  /g, '&nbsp;&nbsp;'),
    )
  },
  onUpdate: (p) => {
    if (!hasArbitraryHtml.value && !isHtmlMode.value) {
      props.context.node.input(
        editor.value.getHTML().replace(/  /g, '&nbsp;&nbsp;'),
      )
    }

    // props.context.node.input(
    //   editor.value.getHTML().replace(/  /g, '&nbsp;&nbsp;'),
    // )
  },
})

if (hasArbitraryHtml.value) {
  isHtmlMode.value = true
}

// Sync external value changes (e.g. AI fill writing into the FormKit node)
// into the TipTap editor + HTML textarea. Guard against the feedback loop
// from the editor's own onUpdate by comparing the incoming value with the
// editor's current HTML before calling setContent.
watch(
  () => props.context._value,
  (incoming) => {
    if (incoming == null) return
    if (!editor.value) {
      htmlContent.value = incoming
      return
    }
    const current = editor.value.getHTML().replace(/  /g, '&nbsp;&nbsp;')
    if (current === incoming) return
    editor.value.commands.setContent(incoming, false, {
      preserveWhitespace: 'full',
    })
    htmlContent.value = incoming
  },
)

// if (props.context.value?.includes('<script>')) {
//   htmlContent.value = props.context.value
//   isHtmlMode.value = true
// }

const toggleHtmlMode = () => {
  if (hasArbitraryHtml.value) {
    notify({
      title: 'HTML Mode Only: You cannot edit arbitrary HTML content.',
      type: 'error',
    })
    return
  }
  if (isHtmlMode.value) {
    console.log('htmlContent', htmlContent.value)
    editor.value?.commands?.setContent(htmlContent.value, false, {
      preserveWhitespace: 'full',
    })
    console.log('editor.value?.getHTML()', editor.value?.getHTML())
  } else {
    htmlContent.value = editor.value?.getHTML().replace(/  /g, '&nbsp;&nbsp;')
  }
  isHtmlMode.value = !isHtmlMode.value
}

watch(htmlContent, () => {
  if (isHtmlMode.value || showRenderedPreview.value) {
    props.context.node.input(htmlContent.value)
  }
})

const openModal = () => {
  isModalOpen.value = true
}
const closeModal = () => {
  isModalOpen.value = false
}

const showRenderedPreview = ref(false)

const togglePreview = () => {
  if (hasArbitraryHtml.value) {
    showRenderedPreview.value = !showRenderedPreview.value
    if (showRenderedPreview.value) {
      isHtmlMode.value = false
    } else {
      isHtmlMode.value = true
    }
  }
}

const handlePreviewInput = (event: Event) => {
  const target = event.target as HTMLElement
  htmlContent.value = target.innerHTML
}

const controls: MenuBarItem[] = [
  {
    icon: 'i-icon-park-outline:code-download',
    title: 'Toggle HTML Mode',
    action: toggleHtmlMode,
    isActive: () => isHtmlMode.value,
  },
  {
    icon: 'i-icon-park-outline:text-bold',
    title: 'Bold',
    action: () => editor.value.chain().focus().toggleBold().run(),
    isActive: () => editor.value.isActive('bold'),
  },
  {
    icon: 'i-icon-park-outline:text-italic',
    title: 'Italic',
    action: () => editor.value.chain().focus().toggleItalic().run(),
    isActive: () => editor.value.isActive('italic'),
  },
  /* {
    icon: "i-icon-park-outline:text-underline",
    title: "Underline",
    action: () => editor.value.chain().focus().toggleUnderline().run(),
    isActive: () => editor.value.isActive("underline"),
  }, */
  {
    icon: 'i-icon-park-outline:strikethrough',
    title: 'Strike',
    action: () => editor.value.chain().focus().toggleStrike().run(),
    isActive: () => editor.value.isActive('strike'),
  },
  {
    icon: 'i-icon-park-outline:code',
    title: 'Code',
    action: () => editor.value.chain().focus().toggleCode().run(),
    isActive: () => editor.value.isActive('code'),
  },
  { title: 'divider' },
  {
    icon: 'i-icon-park-outline:paragraph-alphabet',
    title: 'Paragraph',
    action: () => editor.value.chain().focus().setParagraph().run(),
    // isActive: () => editor.value.isActive("paragraph"),
  },
  {
    icon: 'i-icon-park-outline:h1',
    title: 'Heading 1',
    action: () =>
      editor.value.chain().focus().toggleHeading({ level: 1 }).run(),
    isActive: () => editor.value.isActive('heading', { level: 1 }),
  },
  {
    icon: 'i-icon-park-outline:h2',
    title: 'Heading 2',
    action: () =>
      editor.value.chain().focus().toggleHeading({ level: 2 }).run(),
    isActive: () => editor.value.isActive('heading', { level: 2 }),
  },
  {
    icon: 'i-icon-park-outline:h3',
    title: 'Heading 3',
    action: () =>
      editor.value.chain().focus().toggleHeading({ level: 3 }).run(),
    isActive: () => editor.value.isActive('heading', { level: 3 }),
  },
  {
    icon: 'i-icon-park-outline:level-four-title',
    title: 'Heading 4',
    action: () =>
      editor.value.chain().focus().toggleHeading({ level: 4 }).run(),
    isActive: () => editor.value.isActive('heading', { level: 4 }),
  },
  {
    icon: 'i-icon-park-outline:level-five-title',
    title: 'Heading 5',
    action: () =>
      editor.value.chain().focus().toggleHeading({ level: 5 }).run(),
    isActive: () => editor.value.isActive('heading', { level: 5 }),
  },
  {
    icon: 'i-icon-park-outline:level-six-title',
    title: 'Heading 6',
    action: () =>
      editor.value.chain().focus().toggleHeading({ level: 6 }).run(),
    isActive: () => editor.value.isActive('heading', { level: 6 }),
  },
  { title: 'divider' },

  /* {
    icon: "i-icon-park-outline:to-bottom-one",
    title: "Subscript",
    action: () => editor.value.chain().focus().toggleSubscript().run(),
    isActive: () => editor.value.isActive("subscript"),
  },
  {
    icon: "i-icon-park-outline:to-top-one",
    title: "Superscript",
    action: () => editor.value.chain().focus().toggleSuperscript().run(),
    isActive: () => editor.value.isActive("superscript"),
  }, */
  {
    icon: 'i-icon-park-outline:list-two',
    title: 'Bullet List',
    action: () => editor.value.chain().focus().toggleBulletList().run(),
    isActive: () => editor.value.isActive('bulletList'),
  },
  {
    icon: 'i-icon-park-outline:ordered-list',
    title: 'Ordered List',
    action: () => editor.value.chain().focus().toggleOrderedList().run(),
    isActive: () => editor.value.isActive('orderedList'),
  },
  /* {
    icon: "i-icon-park-outline:list",
    title: "Task List",
    action: () => editor.value.chain().focus().toggleTaskList().run(),
    isActive: () => editor.value.isActive("taskList"),
  },
  {title: "divider"},
  {
    icon: "i-icon-park-outline:link-one",
    title: "Link",
    action: () => editor.value.chain().focus().toggleLink().run(),
    isActive: () => editor.value.isActive("link"),
  }, */
  {
    icon: 'i-icon-park-outline:file-code',
    title: 'Code Block',
    action: () => editor.value.chain().focus().toggleCodeBlock().run(),
    isActive: () => editor.value.isActive('codeBlock'),
  },
  {
    icon: 'i-icon-park-outline:quote',
    title: 'Blockquote',
    action: () => editor.value.chain().focus().toggleBlockquote().run(),
    isActive: () => editor.value.isActive('blockquote'),
  },
  {
    icon: 'i-icon-park-outline:dividing-line-one',
    title: 'Horizontal Rule',
    action: () => editor.value.chain().focus().setHorizontalRule().run(),
  },
  {
    icon: 'i-icon-park-outline:paragraph-break-two',
    title: 'Hard Break',
    action: () => editor.value.chain().focus().setHardBreak().run(),
  },
  { title: 'divider' },
  {
    icon: 'i-icon-park-outline:file-failed-one',
    title: 'Clear Format',
    action: () =>
      editor.value.chain().focus().clearNodes().unsetAllMarks().run(),
  },
  {
    icon: 'i-icon-park-outline:undo',
    title: 'Undo',
    action: () => editor.value.chain().focus().undo().run(),
  },
  {
    icon: 'i-icon-park-outline:redo',
    title: 'Redo',
    action: () => editor.value.chain().focus().redo().run(),
  },
  {
    icon: 'i-icon-park-outline:delete',
    title: 'Remove all Content',
    action: () => editor.value.chain().focus().clearContent().run(),
  },
]
</script>

<template>
  <div v-if="editor" class="flex flex-col divide-y relative">
    <div class="flex items-center flex-wrap p-2 gap-1">
      <div
        v-for="item in controls"
        :key="item.title"
        :class="{ 'w-[2px] h-[1.25rem] bg-gray-200 ml-1 mr-1': !item.icon }"
      >
        <button
          v-if="item.icon"
          :class="
            item.isActive && item.isActive()
              ? 'bg-gray-700 text-white'
              : 'text-gray-600'
          "
          :title="item.title"
          class="menu-item rounded-lg p-1.5 hover:bg-gray-700 hover:text-white"
          type="button"
          @click="item.action"
        >
          <div :class="item.icon" class="text-sm" />
        </button>
      </div>
    </div>
    <button
      v-if="hasArbitraryHtml"
      :title="showRenderedPreview ? 'Show Code' : 'Show Preview'"
      :class="showRenderedPreview ? 'bg-gray-700 text-white' : 'text-gray-600'"
      class="menu-item rounded-lg p-1.5 hover:bg-gray-700 hover:text-white absolute bottom-2 left-2"
      type="button"
      @click="togglePreview"
    >
      <div
        :class="
          showRenderedPreview
            ? 'i-icon-park-outline:code'
            : 'i-icon-park-outline:preview-open'
        "
        class="text-sm"
      />
    </button>
    <button
      title="Open in modal"
      class="menu-item rounded-lg p-1.5 hover:bg-gray-700 hover:text-white absolute bottom-2 right-2"
      type="button"
      @click="openModal"
    >
      <div class="i-icon-park-outline:full-screen text-sm" />
    </button>
    <div
      v-if="showRenderedPreview && !isModalOpen"
      contenteditable="true"
      class="min-h-[250px] max-h-[300px] border-top focus:ring-gray-300 w-full"
      v-html="htmlContent"
      @input="handlePreviewInput"
    />
    <div v-else-if="!isHtmlMode && !isModalOpen">
      <EditorContent :editor="editor" class="max-h-[300px] overflow-y-auto" />
    </div>
    <textarea
      v-else-if="isHtmlMode && !isModalOpen && !showRenderedPreview"
      v-model="htmlContent"
      class="w-full h-[300px] p-3 font-mono text-sm"
    />
    <teleport to="body">
      <div v-if="isModalOpen" class="fixed inset-0 z-[1000]">
        <div class="absolute inset-0 bg-black/50" @click="closeModal" />
        <div
          role="dialog"
          aria-modal="true"
          class="absolute inset-0 flex items-center justify-center p-4"
        >
          <div
            class="w-full max-w-5xl bg-white rounded-2xl shadow-2xl flex flex-col"
          >
            <div class="p-4 relative">
              <div class="flex items-center flex-wrap p-2 gap-1">
                <div
                  v-for="item in controls"
                  :key="item.title"
                  :class="{
                    'w-[2px] h-[1.25rem] bg-gray-200 ml-1 mr-1': !item.icon,
                  }"
                >
                  <button
                    v-if="item.icon"
                    :class="
                      item.isActive && item.isActive()
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-600'
                    "
                    :title="item.title"
                    class="menu-item rounded-lg p-1.5 hover:bg-gray-700 hover:text-white"
                    type="button"
                    @click="item.action"
                  >
                    <div :class="item.icon" class="text-sm" />
                  </button>
                </div>
              </div>
              <button
                v-if="hasArbitraryHtml"
                :title="showRenderedPreview ? 'Show Code' : 'Show Preview'"
                :class="
                  showRenderedPreview
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-600'
                "
                class="menu-item rounded-lg p-1.5 hover:bg-gray-700 hover:text-white absolute bottom-7 left-6 border"
                type="button"
                @click="togglePreview"
              >
                <div
                  :class="
                    showRenderedPreview
                      ? 'i-icon-park-outline:code'
                      : 'i-icon-park-outline:preview-open'
                  "
                  class="text-sm"
                />
              </button>
              <div
                v-if="showRenderedPreview"
                contenteditable="true"
                class="min-h-[500px] max-h-[50vh] border rounded-lg p-3 focus:ring-gray-300 w-full"
                v-html="htmlContent"
                @input="handlePreviewInput"
              />
              <div v-else-if="!isHtmlMode" class="border rounded-lg">
                <EditorContent
                  :editor="editor"
                  class="min-h-[500px] max-h-[50vh] overflow-y-auto"
                />
              </div>
              <textarea
                v-else-if="!showRenderedPreview"
                v-model="htmlContent"
                class="w-full min-h-[500px] max-h-[50vh] border rounded-lg p-3 font-mono text-sm"
              />
            </div>
            <div class="p-4 border-t flex items-center justify-end gap-2">
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="rounded-lg px-3 py-1.5 text-sm bg-gray-900 text-white hover:bg-black"
                  @click="closeModal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
